import React, { useState } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import MatchesList from "./MatchesList";
import { ageCalculate, decimalToFeetInches } from "../../../utils/utils";

export default function MmCompatibuility() {
  const [groomId, setGroomId] = useState("");
  const [brideId, setBrideId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const baseURL = process.env.REACT_APP_BASE_URL || "";
  const API_URL = process.env.REACT_APP_BASE_URL_API;
  const token = localStorage.getItem("adminToken");

  /* ================= VALIDATION ================= */
  const validate = () => {
    if (!groomId || !brideId) {
      setError("Both Groom ID and Bride ID are required");
      return false;
    }
    if (groomId === brideId) {
      setError("Groom and Bride ID cannot be same");
      return false;
    }
    setError("");
    return true;
  };

  /* ================= CHECK COMPATIBILITY ================= */
  const checkCompatibility = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      setResult(null);

      const res = await fetch(`${API_URL}/api/admin/attribute/matchmaking-compatibility`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          groom_id: groomId,
          bride_id: brideId,
        }),
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || "Compatibility check failed");
      }

      setResult(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
 

  const getFieldVal = (obj) => {
    if (!obj) return "";
    if (typeof obj === "object") {
      return (obj.name || obj.title || "").toString().trim();
    }
    return obj.toString().trim();
  };

  const getFieldId = (obj) => {
    if (!obj) return "";
    if (typeof obj === "object") {
      return (obj._id || "").toString().trim();
    }
    return obj.toString().trim();
  };

  const isEntityMatched = (entity1, entity2) => {
    if (!entity1 || !entity2) return false;

    const id1 = getFieldId(entity1);
    const id2 = getFieldId(entity2);
    if (id1 && id2 && id1 === id2) return true;

    const val1 = getFieldVal(entity1).toLowerCase();
    const val2 = getFieldVal(entity2).toLowerCase();
    if (!val1 || !val2 || val1 === "---" || val2 === "---" || val1 === "n/a" || val2 === "n/a") {
      return false;
    }

    return val1 === val2;
  };

  const isDisabilityMatched = (d1, d2) => {
    const s1 = (d1 || "None").toString().trim().toLowerCase();
    const s2 = (d2 || "None").toString().trim().toLowerCase();

    const isNone1 = s1 === "none" || s1 === "no" || s1 === "" || s1 === "null" || s1 === "undefined";
    const isNone2 = s2 === "none" || s2 === "no" || s2 === "" || s2 === "null" || s2 === "undefined";

    if (isNone1 && isNone2) return true;
    if (isNone1 || isNone2) return false;
    return s1 === s2;
  };

  const groomAge = result?.groomData?.dob ? ageCalculate(result.groomData.dob) : null;
  const brideAge = result?.brideData?.dob ? ageCalculate(result.brideData.dob) : null;
  const isAgeMatch = Boolean(
    groomAge && brideAge && groomAge >= brideAge && (groomAge - brideAge) <= 5
  );

  const groomHeight = result?.groomData?.height ? Number(result.groomData.height) : null;
  const brideHeight = result?.brideData?.height ? Number(result.brideData.height) : null;
  const isHeightMatch = Boolean(
    groomHeight && brideHeight && Math.abs(groomHeight - brideHeight) <= 0.15
  );

  const groomMT = result?.groomData?.mother_tongue || result?.groomData?.partner_mother_tongue;
  const brideMT = result?.brideData?.mother_tongue || result?.brideData?.partner_mother_tongue;

  const sampleResult = {
    groomData: {
      profile_id: result ? result.groomData?.profile_id || '---' : '---',
      name: result ? result.groomData?.name || '---' : '---',
    },
    brideData: {
      profile_id: result ? result.brideData?.profile_id || '---' : '---',
      name: result ? result.brideData?.name || '---' : '---',
    },
    matches: [
      { 
        attribute: "Age", 
        groomValue: result && groomAge ? groomAge : '---', 
        brideValue: result && brideAge ? brideAge : '---', 
        isMatch: isAgeMatch 
      },
      { 
        attribute: "Height", 
        groomValue: result?.groomData?.height ? decimalToFeetInches(result.groomData.height) : '---', 
        brideValue: result?.brideData?.height ? decimalToFeetInches(result.brideData.height) : '---', 
        isMatch: isHeightMatch 
      },
      { 
        attribute: "Marital Status", 
        groomValue: result ? (getFieldVal(result.groomData?.marital_status) || '---') : '---', 
        brideValue: result ? (getFieldVal(result.brideData?.marital_status) || '---') : '---', 
        isMatch: isEntityMatched(result?.groomData?.marital_status, result?.brideData?.marital_status) 
      },
      { 
        attribute: "Disability", 
        groomValue: result ? ((result.groomData?.disability && String(result.groomData.disability).trim()) || 'None') : 'None', 
        brideValue: result ? ((result.brideData?.disability && String(result.brideData.disability).trim()) || 'None') : 'None', 
        isMatch: Boolean(result && isDisabilityMatched(result.groomData?.disability, result.brideData?.disability)) 
      },
      { 
        attribute: "Religion/Community", 
        groomValue: result ? (getFieldVal(result.groomData?.religion) || '---') : '---', 
        brideValue: result ? (getFieldVal(result.brideData?.religion) || '---') : '---', 
        isMatch: isEntityMatched(result?.groomData?.religion, result?.brideData?.religion) 
      },
      { 
        attribute: "Caste", 
        groomValue: result ? (getFieldVal(result.groomData?.caste) || '---') : '---', 
        brideValue: result ? (getFieldVal(result.brideData?.caste) || '---') : '---', 
        isMatch: isEntityMatched(result?.groomData?.caste, result?.brideData?.caste) 
      },
      { 
        attribute: "Mother Tongue", 
        groomValue: result ? (getFieldVal(groomMT) || '---') : '---', 
        brideValue: result ? (getFieldVal(brideMT) || '---') : '---', 
        isMatch: isEntityMatched(groomMT, brideMT) 
      },
      { 
        attribute: "Education", 
        groomValue: result ? (getFieldVal(result.groomData?.highest_degree) || '---') : '---', 
        brideValue: result ? (getFieldVal(result.brideData?.highest_degree) || '---') : '---', 
        isMatch: isEntityMatched(result?.groomData?.highest_degree, result?.brideData?.highest_degree) 
      },
      { 
        attribute: "Occupation", 
        groomValue: result ? (getFieldVal(result.groomData?.occupation) || '---') : '---', 
        brideValue: result ? (getFieldVal(result.brideData?.occupation) || '---') : '---', 
        isMatch: isEntityMatched(result?.groomData?.occupation, result?.brideData?.occupation) 
      },
      { 
        attribute: "State Living In", 
        groomValue: result ? (getFieldVal(result.groomData?.loc_state) || '---') : '---', 
        brideValue: result ? (getFieldVal(result.brideData?.loc_state) || '---') : '---', 
        isMatch: isEntityMatched(result?.groomData?.loc_state, result?.brideData?.loc_state) 
      },
    ],
  };

  const totalMatches   = sampleResult.matches.filter((m) => m.isMatch).length;
  const totalUnmatched = sampleResult.matches.length - totalMatches;
  
  console.log("Sample Result:", result);

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Matchmaking Compatibility" />

        <div className="right_col " role="main">
          

          <div className="x_panel">
                <div className="x_title">
                   <h2>Matchmaking Compatibuility</h2>

                   <div className="clearfix"></div>
                </div>
                <div className="x_content"> 

    <div className="filtercont">
            <div className="row">
              
                <div className="col-lg-10">
              <div className="col-12 col-sm-4">
                <label>Groom ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={groomId}
                  onChange={(e) => setGroomId(e.target.value)}
                />
              </div>

              <div className="col-12 col-sm-4">
                <label>Bride ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={brideId}
                  onChange={(e) => setBrideId(e.target.value)}
                />
              </div>

              <div className="col-12 col-sm-4">
                <label>&nbsp;</label>
                <button
                  className="btn sitebtn w-100"
                  onClick={checkCompatibility}
                  disabled={loading}
                >
                  {loading ? "Checking..." : "Check Compatibility"}
                </button>
              </div>
              </div>
              </div>
            </div>

            {/* ERROR */}
            {error && (
              <div className="text-danger mt-2">
                <p>{error}</p>
              </div>
            )}

            {/* RESULT */}
            {result && (
              <>
                <hr />
                <h5 className="text-center">
                  Match <span className="text-success">{totalMatches}</span>, Unmatched <span className="text-danger">{totalUnmatched}</span>
                </h5>

                <div className="detail-partners-pro">
                <div className="row">
                  <div className="col-12">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="hed-proher-ex">
                          <h4> {result.groomData.name} (#{result.groomData.profile_id})</h4>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mat-pro-2">
                          <h4>{result.brideData.name} (#{result.brideData.profile_id})</h4>
                        </div>
                      </div>
                    </div>
                  </div>

                 <MatchesList sampleResult={sampleResult} baseURL={baseURL} />

                </div>
              </div>

              </>
            )}
          </div>
        </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
